import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PackageMinus, Plus, X, Camera, Search, Trash2, Check, Truck, Image as ImageIcon } from "lucide-react";

const STATI = {
  da_rimpinguare: { label: "DA RIMPINGUARE", cls: "gm-badge-danger" },
  ordinato: { label: "RICAMBIO ORDINATO", cls: "gm-badge-warning" },
  rimontato: { label: "RIMONTATO", cls: "gm-badge-success" },
};

const readPhoto = (file) => new Promise((res, rej) => {
  if (file.size > 1.5 * 1024 * 1024) { rej(new Error("Foto max 1.5MB")); return; }
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const NuovoModal = ({ onClose, onCreated, woId, woNumero }) => {
  const [form, setForm] = useState({ matricola: "", marca: "", modello: "", ricambio_descrizione: "", ricambio_codice: "", descrizione: "" });
  const [foto, setFoto] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      try { const b64 = await readPhoto(f); setFoto(prev => [...prev, b64]); }
      catch (err) { toast.error(err.message); }
    }
    e.target.value = "";
  };

  const save = async () => {
    if (!form.matricola.trim()) { toast.error("Matricola obbligatoria"); return; }
    if (!form.ricambio_descrizione.trim()) { toast.error("Indica il ricambio prelevato"); return; }
    setSaving(true);
    try {
      await api.post("/smontaggi", { ...form, foto, work_order_id: woId || "" });
      toast.success("Smontaggio registrato");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-3 overflow-y-auto" data-testid="sm-nuovo-modal">
      <div className="gm-card w-full max-w-2xl p-5 my-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-black uppercase tracking-tight flex items-center gap-2">
            <PackageMinus className="text-[#FF3B30]" size={20} /> Nuovo smontaggio
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="sm-modal-close"><X size={20} /></button>
        </div>
        {woNumero && (
          <div className="mb-3 p-2.5 border border-[#4CAF50]/40 bg-[#4CAF50]/5 text-sm">
            Collegato alla scheda <b className="font-mono-data text-[#4CAF50]">{woNumero}</b>
          </div>
        )}
        <div className="text-xs text-zinc-500 mb-4">Prelievo di un ricambio da un mezzo nuovo in esposizione. Il mezzo resterà segnato come <b className="text-[#FF3B30]">DA RIMPINGUARE</b> finché il ricambio non viene rimontato.</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Matricola mezzo *</label>
            <input value={form.matricola} onChange={set("matricola")} className="gm-input font-mono-data" placeholder="Es. WNCE1902E00000755" data-testid="sm-matricola" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Marca</label>
            <input value={form.marca} onChange={set("marca")} className="gm-input" placeholder="Es. Wacker Neuson" data-testid="sm-marca" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Modello</label>
            <input value={form.modello} onChange={set("modello")} className="gm-input" placeholder="Es. EZ50" data-testid="sm-modello" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Ricambio prelevato *</label>
            <input value={form.ricambio_descrizione} onChange={set("ricambio_descrizione")} className="gm-input" placeholder="Es. Pompa idraulica principale" data-testid="sm-ricambio" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Codice ricambio</label>
            <input value={form.ricambio_codice} onChange={set("ricambio_codice")} className="gm-input font-mono-data" placeholder="Opzionale" data-testid="sm-codice" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Descrizione / Note</label>
            <textarea value={form.descrizione} onChange={set("descrizione")} rows={3} className="gm-input" placeholder="Es. prelevato per riparazione urgente, mezzo in piazzale lato nord..." data-testid="sm-descrizione" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
              <span>Foto ({foto.length})</span>
              <label className="text-[#4CAF50] cursor-pointer flex items-center gap-1 normal-case tracking-normal text-xs font-bold" data-testid="sm-add-foto">
                <Camera size={14} /> Scatta / aggiungi foto
                <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhoto} />
              </label>
            </label>
            {foto.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-2">
                {foto.map((f, i) => (
                  <div key={i} className="relative">
                    <img src={f} alt="" className="h-20 w-full object-cover border border-zinc-800" />
                    <button onClick={() => setFoto(p => p.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 bg-black/70 text-white p-0.5"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="h-11 px-5 border border-zinc-700 text-zinc-300 uppercase tracking-widest text-xs font-bold rounded-sm">Annulla</button>
          <button onClick={save} disabled={saving} className="gm-btn-primary h-11 px-6 rounded-sm flex items-center gap-2 disabled:opacity-50" data-testid="sm-salva">
            <Check size={16} /> {saving ? "Salvataggio..." : "Registra smontaggio"}
          </button>
        </div>
      </div>
    </div>
  );
};

const FotoModal = ({ smId, onClose, onChanged }) => {
  const [sm, setSm] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/smontaggi/${smId}`); setSm(data); }
    catch { toast.error("Errore caricamento"); }
  }, [smId]);
  useEffect(() => { load(); }, [load]);

  const addPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const b64 = await readPhoto(file);
      await api.post(`/smontaggi/${smId}/foto`, { foto: b64 });
      toast.success("Foto aggiunta"); load(); onChanged();
    } catch (err) { toast.error(err.message || "Errore upload"); }
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-3 overflow-y-auto" data-testid="sm-foto-modal">
      <div className="gm-card w-full max-w-3xl p-5 my-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-black uppercase tracking-tight">Foto — {sm?.numero || ""}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="sm-foto-close"><X size={20} /></button>
        </div>
        <label className="text-xs uppercase tracking-widest text-[#4CAF50] cursor-pointer flex items-center gap-1 mb-3 w-fit" data-testid="sm-foto-add">
          <Camera size={14} /> Aggiungi foto
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addPhoto} />
        </label>
        {!sm ? <div className="text-zinc-500 text-sm">Caricamento...</div> : (
          sm.foto?.length === 0 ? <div className="text-sm text-zinc-500">Nessuna foto</div> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {sm.foto.map((f, i) => (
                <a key={i} href={f} target="_blank" rel="noreferrer">
                  <img src={f} alt="" className="h-36 w-full object-cover border border-zinc-800 hover:border-[#4CAF50]" />
                </a>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default function SmontaggiPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stato, setStato] = useState("aperti");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(params.get("nuovo") === "1");
  const [fotoOf, setFotoOf] = useState(null);
  const woId = params.get("wo") || "";
  const woNumero = params.get("wo_numero") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/smontaggi", { params: { stato: stato === "tutti" ? undefined : stato, q: q || undefined } });
      setItems(data.items);
    } catch { toast.error("Errore caricamento smontaggi"); }
    finally { setLoading(false); }
  }, [stato, q]);
  useEffect(() => { load(); }, [load]);

  const setStatoSm = async (id, nuovo) => {
    try { await api.put(`/smontaggi/${id}`, { stato: nuovo }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo smontaggio?")) return;
    try { await api.delete(`/smontaggi/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeNew = () => { setShowNew(false); setParams({}, { replace: true }); };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Prelievi da mezzi nuovi in esposizione</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">Smontaggi</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="gm-btn-primary h-12 px-6 rounded-sm flex items-center gap-2" data-testid="sm-nuovo-btn">
          <Plus size={18} /> Nuovo smontaggio
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[["aperti", "Aperti"], ["da_rimpinguare", "Da rimpinguare"], ["ordinato", "Ordinato"], ["rimontato", "Rimontato"], ["tutti", "Tutti"]].map(([k, l]) => (
          <button key={k} onClick={() => setStato(k)} className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border rounded-sm ${stato === k ? "border-[#4CAF50] text-[#4CAF50] bg-[#4CAF50]/10" : "border-zinc-800 text-zinc-500 hover:text-white"}`} data-testid={`sm-filtro-${k}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca matricola, ricambio, numero..." className="gm-input pl-9" data-testid="sm-search" />
        </div>
      </div>

      {loading ? <div className="text-zinc-500 uppercase tracking-widest text-sm p-6">Caricamento...</div> : items.length === 0 ? (
        <div className="gm-card p-10 text-center text-zinc-500" data-testid="sm-empty">
          <PackageMinus size={36} className="mx-auto mb-3 text-zinc-700" />
          Nessuno smontaggio {stato !== "tutti" ? "in questo stato" : "registrato"}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(sm => {
            const st = STATI[sm.stato] || STATI.da_rimpinguare;
            return (
              <div key={sm.id} className={`gm-card p-4 ${sm.stato === "da_rimpinguare" ? "border-[#FF3B30]/40" : ""}`} data-testid={`sm-card-${sm.numero}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono-data text-sm text-[#4CAF50]">{sm.numero}</span>
                      <span className={`gm-badge ${st.cls}`}>{st.label}</span>
                      {sm.wo_numero && <Link to={`/schede/${sm.work_order_id}`} className="gm-badge gm-badge-muted hover:border-[#4CAF50]">Scheda {sm.wo_numero}</Link>}
                    </div>
                    <div className="font-display text-lg font-bold uppercase tracking-tight">
                      {[sm.marca, sm.modello].filter(Boolean).join(" ") || "Mezzo"} <span className="font-mono-data text-sm text-zinc-400 normal-case">• {sm.matricola}</span>
                    </div>
                    <div className="text-sm text-zinc-300 mt-1">
                      <Truck size={13} className="inline mr-1 text-zinc-500" />
                      Prelevato: <b>{sm.ricambio_descrizione}</b>{sm.ricambio_codice && <span className="font-mono-data text-zinc-400"> ({sm.ricambio_codice})</span>}
                    </div>
                    {sm.descrizione && <div className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{sm.descrizione}</div>}
                    <div className="text-[11px] text-zinc-600 mt-2">
                      Aperto da {sm.created_by_name} il {new Date(sm.created_at).toLocaleString("it-IT")}
                      {sm.stato === "rimontato" && sm.closed_by_name && <> — Rimontato da {sm.closed_by_name} il {new Date(sm.closed_at).toLocaleString("it-IT")}</>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <button onClick={() => setFotoOf(sm.id)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1 border border-zinc-800 px-3 py-1.5 rounded-sm" data-testid={`sm-foto-btn-${sm.numero}`}>
                      <ImageIcon size={13} /> Foto ({sm.foto_count || 0})
                    </button>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      {sm.stato === "da_rimpinguare" && (
                        <button onClick={() => setStatoSm(sm.id, "ordinato")} className="text-[11px] uppercase tracking-widest font-bold text-[#FF9F0A] border border-[#FF9F0A]/40 px-3 py-1.5 rounded-sm hover:bg-[#FF9F0A]/10" data-testid={`sm-ordina-${sm.numero}`}>Ricambio ordinato</button>
                      )}
                      {sm.stato !== "rimontato" && (
                        <button onClick={() => setStatoSm(sm.id, "rimontato")} className="text-[11px] uppercase tracking-widest font-bold text-[#4CAF50] border border-[#4CAF50]/40 px-3 py-1.5 rounded-sm hover:bg-[#4CAF50]/10" data-testid={`sm-rimonta-${sm.numero}`}>Rimontato ✓</button>
                      )}
                      {sm.stato === "rimontato" && (
                        <button onClick={() => setStatoSm(sm.id, "da_rimpinguare")} className="text-[11px] uppercase tracking-widest font-bold text-zinc-400 border border-zinc-700 px-3 py-1.5 rounded-sm hover:text-white" data-testid={`sm-riapri-${sm.numero}`}>Riapri</button>
                      )}
                      {["admin", "ufficio_acquisti"].includes(user?.role) && (
                        <button onClick={() => del(sm.id)} className="text-[11px] text-[#FF3B30] border border-[#FF3B30]/40 px-2.5 py-1.5 rounded-sm hover:bg-[#FF3B30]/10" data-testid={`sm-elimina-${sm.numero}`}><Trash2 size={13} /></button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NuovoModal onClose={closeNew} onCreated={() => { closeNew(); load(); }} woId={woId} woNumero={woNumero} />}
      {fotoOf && <FotoModal smId={fotoOf} onClose={() => setFotoOf(null)} onChanged={load} />}
    </div>
  );
}
